import { Navigate, useLocation } from 'react-router-dom'
import { useProfile } from '@/lib/hooks/use-profile'
import { useIsAdmin } from '@/lib/hooks/use-is-admin'

interface OnboardingGuardProps {
  children: React.ReactNode
}

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const location = useLocation()
  const { profile, isLoading } = useProfile()
  const isAdmin = useIsAdmin()

  // Show loading spinner while profile is loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--color-4))]" />
      </div>
    )
  }

  // Wait for profile to load
  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(var(--color-4))]" />
      </div>
    )
  }

  const isOnboardingRoute = location.pathname === '/onboarding'

  // IMPORTANT: Block rendering and redirect to onboarding if not completed
  // Using Navigate component instead of useEffect prevents race condition
  if (!profile.onboarding_completed && !isOnboardingRoute) {
    return <Navigate to="/onboarding" replace />
  }

  // Redirect users who have completed onboarding away from the onboarding page
  // Note: For freelancers, ProfileEnabledGuard will handle disabled profile redirects
  if (profile.onboarding_completed && isOnboardingRoute) {
    if (isAdmin) {
      return <Navigate to="/admin?tab=freelancers" replace />
    } else if (profile.is_client) {
      return <Navigate to="/client/dashboard" replace />
    } else if (profile.is_genius) {
      return <Navigate to="/genius/dashboard" replace />
    } else {
      return <Navigate to="/" replace />
    }
  }

  return <>{children}</>
}
